import pino, { Logger, Level as PinoLevel, LoggerOptions } from 'pino';
import dashify from 'dashify';
import './window';

export type Level = PinoLevel;

const isError = function (obj: unknown) {
  return Object.prototype.toString.call(obj) === '[object Error]';
};

const wrapMethod = (target: Logger, member: Level) => {
  return <T extends string>(
    key: T extends '' ? never : T,
    message: string,
    details?: Record<string, unknown> | Error
  ) => {
    target[member](
      {
        key: dashify(key, { condense: true }).toUpperCase(),
        ...(isError(details) ? { err: details } : details)
      },
      message
    );
  };
};

export function generateLogger(logLevel: Level, additionalOptions?: LoggerOptions) {
  const level =
    logLevel ||
    (typeof process !== 'undefined' && process.env?.LOG_LEVEL) ||
    (typeof window !== 'undefined' && window.__ENV__ != null && window.__ENV__.LOG_LEVEL) ||
    'info';
  const pinoConfiguration: LoggerOptions = {
    level,
    messageKey: 'message',
    browser: { asObject: true },
    ...additionalOptions
  };

  const pinoLogger = pino(pinoConfiguration);

  const logger: Record<Level, ReturnType<typeof wrapMethod>> = {
    trace: wrapMethod(pinoLogger, 'trace'),
    debug: wrapMethod(pinoLogger, 'debug'),
    info: wrapMethod(pinoLogger, 'info'),
    warn: wrapMethod(pinoLogger, 'warn'),
    error: wrapMethod(pinoLogger, 'error'),
    fatal: wrapMethod(pinoLogger, 'fatal')
  } as const;

  return logger;
}
